import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { trackPageView, setUserId } from './lib/analytics';
import { HelmetProvider } from 'react-helmet-async';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogEditor from './pages/BlogEditor';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Search from './pages/Search';
import BookAppointment from './pages/BookAppointment';
import PatientDashboard from './pages/PatientDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import OnboardingWizard from './pages/OnboardingWizard';
import ProviderSignup from './pages/ProviderSignup';
import ProviderProfile from './pages/ProviderProfile';
import AdminDashboard from './pages/AdminDashboard';
import ClinicDashboard from './pages/ClinicDashboard';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect based on role
    if (user?.role === 'admin') return <Navigate to="/physign-admin-panel" replace />;
    if (['doctor', 'nurse', 'lab'].includes(user?.role)) return <Navigate to="/provider" replace />;
    if (user?.role === 'clinic') return <Navigate to="/clinic" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const { user } = useAuth();

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Set user ID on login
  useEffect(() => {
    if (user?.id) setUserId(user.id, user.role);
  }, [user]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/blog-editor" element={<ProtectedRoute allowedRoles={['admin', 'doctor']}><BlogEditor /></ProtectedRoute>} />
      <Route path="/blog-editor/:blogId" element={<ProtectedRoute allowedRoles={['admin', 'doctor']}><BlogEditor /></ProtectedRoute>} />
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/provider-signup" element={<ProviderSignup />} />
      <Route path="/doctor/register" element={<ProviderSignup />} />
      <Route path="/search" element={<Search />} />
      {/* SEO-friendly URLs — same Search component with path-based filters */}
      <Route path="/doctors" element={<Search />} />
      <Route path="/nurses" element={<Search />} />
      <Route path="/home-visit" element={<Search />} />
      <Route path="/book-appointment" element={<Search />} />
      <Route path="/provider/:providerId" element={<ProviderProfile />} />
      
      {/* Protected Routes */}
      <Route 
        path="/book/:providerId" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <BookAppointment />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/provider" 
        element={
          <ProtectedRoute allowedRoles={['doctor', 'nurse', 'lab']}>
            <ProviderDashboard />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/provider/onboarding"
        element={
          <ProtectedRoute allowedRoles={['doctor', 'nurse']}>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />
      
      <Route 
        path="/clinic" 
        element={
          <ProtectedRoute allowedRoles={['clinic']}>
            <ClinicDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin" 
        element={<AdminDashboard />}
      />
      
      <Route 
        path="/physign-admin-panel" 
        element={<AdminDashboard />}
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              className: 'font-sans',
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
